import { AuthenticateApiBaseService } from '@services/api/authenticate-api-base.service'
import { Subject, Subscription, of, lastValueFrom, throwError, finalize } from 'rxjs'
import { map, tap, switchMap, catchError } from 'rxjs/operators'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { User, UserSearch } from '@models/user.model'
import { Authenticate } from '@models/auth.model'
import { environment } from '@env/environment'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import moment from 'moment/moment'
import 'moment-timezone'

export const SESSION_PROFILE_KEY = '__session_profile'

export const SESSION_USER_KEY = '__session_user'
export const DEVICE_KEY_STORAGE = '__device_key'
export const DEVICE_GROUP_KEY_STORAGE = '__device_group_key'
export const DEVICE_PASSWORD_STORAGE = '__device_password'

@Injectable({
    providedIn: 'root'
})
export class AuthService extends AuthenticateApiBaseService {
    private readonly source: string
    private sessionScheduler: Subscription
    private sessionSchedulerDestroy$ = new Subject<void>()
    private _session: Authenticate | null = null

    logoutSubmitted: boolean

    constructor(private router: Router,
                override http: HttpClient
    ) {
        super(http)
        this.source = ''
        this.searchTerm = new UserSearch()
    }

    bootstrapSession() {
        const username = this.sessionUsername

        if (!username) {
            this.clearSession()
            return Promise.resolve()
        }

        return lastValueFrom(
            this.refreshSession().pipe(
                map(() => undefined),
                catchError(() => {
                    this.clearSession()
                    return of(undefined)
                })
            )
        )
    }

    login(input: { username: string; password: string; newPassword?: string }) {
        return this.post<
            {
                username: string
                password: string
                newPassword?: string
            },
            Authenticate
        >(`${this.source}/signin`, input).pipe(
            tap(() => this.resetSession(false)),
            map(response => {
                return response
            })
        )
    }

    loginV2(input: { username: string; password: string; newPassword?: string }) {
        return this.http
            .post<Authenticate>(`${this.url}/v2/signin`, input, { withCredentials: true })
            .pipe(
                tap(() => this.resetSession(false)),
                map(response => {
                    const session = new Authenticate({ ...response, username: response.username ?? input.username })
                    this.setSession(session)
                    return session
                }),
                switchMap(session => {
                    if (session.deviceKey && session.deviceGroupKey) {
                        return this.confirmDevice(session.token, session.deviceKey, session.deviceGroupKey).pipe(
                            switchMap(confirm => this.rememberDevice(session.token, confirm.deviceKey).pipe(map(() => confirm))),
                            map(confirm => {
                                this.storeDevice(confirm)
                                return session
                            })
                        )
                    }

                    return of(session)
                })
            )
    }

    logout() {
        this.logoutSubmitted = true
        this.http.post(`${this.url}/v2/logout`, {}, { withCredentials: true })
            .pipe(
                finalize(() => (this.logoutSubmitted = false)),
                catchError(() => of(null))
            )
            .subscribe(() => {
                this.resetSession(false).then()
            })
    }

    profile(token: string) {
        return this.get<User>(`${this.source}/users/profile`, null, new HttpHeaders().append('Authorization', `Bearer ${token}`)).pipe(
            tap({
                next: (response: User) => {
                    localStorage.setItem(SESSION_PROFILE_KEY, JSON.stringify(response))
                }
            })
        )
    }

    redirectToBoard(response: Authenticate) {
        const session = btoa(JSON.stringify(response))
        window.open(`${environment.urlLogin}/auth/signin?session=${session}`, '_self')
    }

    refreshSession() {
        const username = this.sessionUsername

        if (!username) {
            return throwError(() => new Error('Missing username for refresh.'))
        }

        return this.http
            .post<Authenticate>(`${this.url}/v2/refresh`, { username }, { withCredentials: true })
            .pipe(
                map(response => {
                    const session = new Authenticate({ ...response, username })
                    this.setSession(session)
                    return session
                })
            )
    }

    confirmDevice(accessToken: string, deviceKey: string, deviceGroupKey: string, deviceName?: string) {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`)
        return this.http.post<{ deviceKey: string; deviceGroupKey: string; devicePassword: string }>(
            `${this.url}/devices/confirm`,
            { deviceKey, deviceGroupKey, deviceName },
            { headers, withCredentials: true }
        )
    }

    rememberDevice(accessToken: string, deviceKey: string) {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`)
        return this.http.post<{ message: string }>(
            `${this.url}/devices/remember`,
            { deviceKey },
            { headers, withCredentials: true }
        )
    }

    forgetDevice(accessToken: string, deviceKey: string) {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`)
        return this.http.post<{ message: string }>(
            `${this.url}/devices/forget`,
            { deviceKey },
            { headers, withCredentials: true }
        )
    }

    async resetSession(redirect = true) {
        if (redirect && this.session.token) {
            window.open(`${environment.urlLogin}/auth/signin`, '_self')
        }

        this.clearSession()
        this.stopUserExpirationScheduler()
    }

    private stopUserExpirationScheduler() {
        this.sessionSchedulerDestroy$.next()
        this.sessionSchedulerDestroy$.complete()

        if (this.sessionScheduler) {
            this.sessionScheduler.unsubscribe()
        }
    }

    private get sessionUsername(): string | null {
        return this._session?.username ?? localStorage.getItem(SESSION_USER_KEY)
    }

    private setSession(session: Authenticate | null) {
        this._session = session

        if (session?.username) {
            localStorage.setItem(SESSION_USER_KEY, session.username)
        }

        if (session?.deviceKey && session?.deviceGroupKey) {
            this.storeDevice({
                deviceKey: session.deviceKey,
                deviceGroupKey: session.deviceGroupKey,
                devicePassword: session.devicePassword
            })
        }
    }

    private clearSession() {
        this._session = null
        localStorage.removeItem(SESSION_USER_KEY)
        localStorage.removeItem(SESSION_PROFILE_KEY)
    }

    private storeDevice(device: { deviceKey: string; deviceGroupKey: string; devicePassword?: string }) {
        localStorage.setItem(DEVICE_KEY_STORAGE, device.deviceKey)
        localStorage.setItem(DEVICE_GROUP_KEY_STORAGE, device.deviceGroupKey)

        if (device.devicePassword) {
            localStorage.setItem(DEVICE_PASSWORD_STORAGE, device.devicePassword)
        }
    }

    private clearDevice() {
        localStorage.removeItem(DEVICE_KEY_STORAGE)
        localStorage.removeItem(DEVICE_GROUP_KEY_STORAGE)
        localStorage.removeItem(DEVICE_PASSWORD_STORAGE)
    }

    get user$() {
        return of(JSON.parse(localStorage.getItem(SESSION_PROFILE_KEY) || '{}') as User)
    }

    get headersDefault() {
        return new HttpHeaders().append('Authorization', `Bearer ${this.token}`)
    }

    get token(): string | null {
        return this._session?.token || null
    }

    get expired(): boolean {
        if (this.session.expireAt) {
            const expired = moment(this.session.expireAt, 'YYYY-MM-DDTHH:mm:ss.SSSS').subtract(4, 'minutes')
            return expired.isBefore(this.dateNow)
        }

        return true
    }

    get session(): Authenticate {
        return this._session ?? new Authenticate()
    }

    get dateNow() {
        return moment.tz('America/Fortaleza')
    }
}
